import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import type { AppBundle } from '../utils/app-bundles';

interface AppBundleData {
  initialized: boolean;
  loaded: boolean;
  appWrapper: HTMLDivElement;
  cleanup: () => void;
  loadPromise: Promise<void>;
}

interface BackgroundAppBundleContextType {
  getBackgroundAppData: (appId: AppBundle) => AppBundleData;
  setBackgroundAppData: (appId: AppBundle, data: Partial<AppBundleData>) => void;
}

const BackgroundAppBundleContext = createContext<BackgroundAppBundleContextType | undefined>(undefined);

export function useBackgroundAppBundle(appId: AppBundle) {
  const context = useContext(BackgroundAppBundleContext);
  if (context === undefined) {
    throw new Error('useBackgroundAppBundle must be used within an BackgroundAppBundleContextProvider');
  }

  return {
    getData: () => context.getBackgroundAppData(appId),
    setData: (data: Partial<AppBundleData>) => context.setBackgroundAppData(appId, data),
  };
}

/** This context is used to store data about app bundles that are loaded in the background.
 * More info about this behavior can be found in the AppBundleLoader component. */
export function BackgroundAppBundleContextProvider({ children }: { children: ReactNode }) {
  const appDataMapRef = useRef<Record<AppBundle, AppBundleData>>({} as Record<AppBundle, AppBundleData>);

  const getBackgroundAppData = useCallback((appId: AppBundle) => {
    return appDataMapRef.current[appId];
  }, []);

  const setBackgroundAppData = useCallback((appId: AppBundle, data: Partial<AppBundleData>) => {
    appDataMapRef.current = {
      ...appDataMapRef.current,
      [appId]: {
        ...appDataMapRef.current[appId],
        ...data,
      },
    };
  }, []);

  /** Cleans up app wrappers and app data when this entire context is unmounted
   * (not just when an app bundle is unmounted) */
  useEffect(function cleanupOnUnmount() {
    return () => {
      for (const appData of Object.values(appDataMapRef.current)) {
        if (appData.appWrapper != null) {
          appData.appWrapper.remove();
        }
        if (appData.cleanup != null) {
          appData.cleanup();
        }
      }
    };
  }, []);

  return (
    <BackgroundAppBundleContext.Provider value={{ getBackgroundAppData, setBackgroundAppData }}>
      {children}
    </BackgroundAppBundleContext.Provider>
  );
}
